import { AccountStatuses, BreadAccount } from "breadcommon";
import {
  firestoreUpdateAccount,
  firestoreCloseAccount,
  firestoreReopenAccount,
} from "../firebaseio/firestoreIo";
import { useContext, useState } from "react";
import { UserContext } from "../firebaseio/UserContext";
import { AccountsContext } from "../firebaseio/AccountsContext";
import { RyeModalContent, useRyeModal } from "../rye/RyeModal";
import { RyeInput } from "../rye/RyeInput";
import { RyeButton } from "../rye/RyeButton";
import { buildClasses } from "../utils/buildClasses";
import React from "react";
import { DeleteAccountModal } from "./DeleteAccountModal";
import { manualAccountName } from "./accountName";
import { RyeReveal } from "../rye/RyeReveal";

export function AccountSettingsModal(props: {
  initialAccount: BreadAccount;
}): JSX.Element {
  const { closeModal, openModal } = useRyeModal();
  const [newAccountName, setNewAccountName] = useState(
    props.initialAccount.name,
  );
  const user = useContext(UserContext);
  const accounts = useContext(AccountsContext);

  const isClosed = props.initialAccount.status === AccountStatuses.CLOSED;

  return (
    <RyeModalContent
      title="Account Settings"
      actionButtons={[
        {
          text: "Cancel",
          variant: "outlined",
          vibe: "subdued",
          action: () => {},
        },
        {
          text: "Save",
          variant: "filled",
          vibe: "primary",
          action: () => {
            if (user) {
              firestoreUpdateAccount(user.uid, props.initialAccount.id, {
                manual_data: {
                  name: manualAccountName(
                    newAccountName,
                    props.initialAccount.original_name,
                  ),
                },
              });
            }
          },
        },
      ]}
      content={
        <div className={buildClasses("w-[28rem]", "flex", "flex-col", "gap-6")}>
          {/* Account Name */}
          <div className={buildClasses("flex", "flex-col")}>
            <div
              className={buildClasses(
                "text-sm",
                "font-medium",
                "text-on-surface-700",
                "mb-2",
              )}
            >
              Account Name
            </div>
            <RyeInput
              startingText={newAccountName}
              onChange={(text) => setNewAccountName(text)}
              placeholderText={props.initialAccount.original_name}
              width="full"
            />
            <RyeReveal
              show={
                manualAccountName(
                  newAccountName,
                  props.initialAccount.original_name,
                ) !== null
              }
            >
              <div
                className={buildClasses(
                  "flex",
                  "items-center",
                  "justify-between",
                  "gap-4",
                  "pt-2",
                  "text-sm",
                  "text-on-surface-500",
                )}
              >
                <div className={buildClasses("truncate")}>
                  {"Original synced name: "}
                  <i>{props.initialAccount.original_name}</i>
                </div>
                <RyeButton
                  text="Reset"
                  size="sm"
                  variant="transparent"
                  vibe="subdued"
                  onClick={() =>
                    setNewAccountName(props.initialAccount.original_name)
                  }
                />
              </div>
            </RyeReveal>
          </div>

          {/* Close/Reopen Account Section */}
          <div className={buildClasses("flex", "flex-col")}>
            <div
              className={buildClasses(
                "text-sm",
                "font-medium",
                "text-on-surface-700",
                "mb-2",
              )}
            >
              {isClosed ? "Reopen Account" : "Close Account"}
            </div>
            <div
              className={buildClasses(
                "flex",
                "items-center",
                "justify-between",
                "p-4",
                "border",
                "border-on-surface-200",
                "rounded-lg",
                "gap-8",
              )}
            >
              <div
                className={buildClasses(
                  "text-sm",
                  "text-on-surface-500",
                  "flex-1",
                )}
              >
                {isClosed
                  ? "This account is currently marked as closed. Reopening it will resume syncing new transactions and restore it to your active list."
                  : "Mark this account as closed. Historical transactions will be kept, but the account will stop syncing new transactions and the account balance won't be used to calculate your net total. You will be able to re-open the account if you want."}
              </div>
              {isClosed ? (
                <RyeButton
                  text="Reopen"
                  vibe="ordinary"
                  variant="outlined"
                  onClick={async () => {
                    if (!user) return;
                    const activeAccountsCount = Array.from(
                      accounts.values(),
                    ).filter(
                      (acc) => acc.status === AccountStatuses.ACTIVE,
                    ).length;
                    await firestoreReopenAccount(
                      user.uid,
                      props.initialAccount.id,
                      activeAccountsCount,
                    );
                    closeModal();
                  }}
                />
              ) : (
                <RyeButton
                  text="Close account"
                  vibe="ordinary"
                  variant="outlined"
                  onClick={async () => {
                    if (!user) return;
                    await firestoreCloseAccount(
                      user.uid,
                      props.initialAccount.id,
                      Array.from(accounts.values()),
                    );
                    closeModal();
                  }}
                />
              )}
            </div>
          </div>

          {/* Delete Account */}
          <div className={buildClasses("flex", "flex-col")}>
            <div
              className={buildClasses(
                "text-sm",
                "font-medium",
                "text-on-surface-700",
                "mb-2",
              )}
            >
              Delete Account
            </div>
            <div
              className={buildClasses(
                "flex",
                "items-center",
                "justify-between",
                "p-4",
                "border",
                "border-on-surface-200",
                "rounded-lg",
                "gap-8",
              )}
            >
              <div
                className={buildClasses(
                  "text-sm",
                  "text-on-surface-500",
                  "flex-1",
                )}
              >
                Unlink account and delete all related data (including
                transactions).
              </div>
              <RyeButton
                text="Delete account"
                vibe="danger"
                variant="filled"
                onClick={() => {
                  openModal(
                    <DeleteAccountModal
                      account={props.initialAccount}
                      onCancel={() => {
                        openModal(
                          <AccountSettingsModal
                            initialAccount={props.initialAccount}
                          />,
                        );
                      }}
                    />,
                  );
                }}
              />
            </div>
          </div>
        </div>
      }
    />
  );
}
